import { ApolloClient, ApolloLink, from, HttpLink, InMemoryCache, Observable, split } from '@apollo/client';
import { ApolloProvider } from "@apollo/client/react";
import { getMainDefinition } from '@apollo/client/utilities';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';

import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom';
import AuthenticatedApp from './components/routing/AuthenticatedApp';
import { GRAPHQL_ENDPOINT } from './constants/graphqlEndpoint';
import { storePathForRedirect } from './constants/redirectPaths';
import { AuthProvider } from './contexts/AuthContext';
import { ensureValidToken } from './services/TokenRefreshService';
import { TokenService } from './services/TokenService';
import { BannerProvider } from './utils/BannerContext';
import { getAllCachedData, queryClient } from './utils/QueryClient';
import { ToastProvider } from './utils/ToastContext';
import { SSELink } from './utils/sseLink';

// ========================================
// CRITICAL: Store path BEFORE any React code runs
// This must happen before React Router processes routes
// ========================================
const hasAuthenticatedSession = TokenService.isAuthenticated();

if (!hasAuthenticatedSession) {
  storePathForRedirect();
}
// ========================================

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      }),
      Sentry.replayIntegration(),
      Sentry.captureConsoleIntegration({ levels: ['error', 'warn'] })
    ],
    beforeSend: (event, hint) => {
      if (hint.originalException) {
        const allCachedData = getAllCachedData()
        event.extra = {
          ...event.extra,
          cachedData: allCachedData
        }
      }
      return event
    },
    // Performance Monitoring
    tracesSampleRate: 0.3, // Capture 100% of the transactions, reduce in production!
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  })
}

// Detect mobile for logging
const isMobileBrowser = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

// Operations that should NOT trigger token refresh (they are part of the auth flow itself)
const AUTH_OPERATIONS = ['TokenByBankId', 'TokenByRefreshToken', 'AuthSubscription'];

// Custom auth link that handles async token refresh before each request
// Using ApolloLink with Observable instead of deprecated setContext async
const authLink = new ApolloLink((operation, forward) => {
  const isAuthOperation = AUTH_OPERATIONS.includes(operation.operationName || '');

  return new Observable(observer => {
    // Function to proceed with the request after token check
    const proceedWithRequest = () => {
      const token = TokenService.getToken();

      // Log mutation details on mobile
      if (isMobileBrowser() && operation.operationName === 'TokenByBankId') {
        console.log('MOBILE GraphQL Mutation:', {
          operationName: operation.operationName,
          variables: operation.variables,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': token ? 'Bearer [present]' : '[none]'
          }
        });
      }

      // Set headers
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      }));

      // Forward to next link
      const subscription = forward(operation).subscribe({
        next: observer.next.bind(observer),
        error: observer.error.bind(observer),
        complete: observer.complete.bind(observer),
      });

      return () => subscription.unsubscribe();
    };

    // Skip token refresh for auth-related operations to avoid infinite loops
    if (isAuthOperation) {
      return proceedWithRequest();
    }

    // Ensure valid token before proceeding (async)
    const refreshThreshold = 5; // Minutes before expiry to trigger refresh
    ensureValidToken(refreshThreshold)
      .then(() => proceedWithRequest())
      .catch(error => {
        console.error('Token refresh failed:', error);
        // Still try to proceed - maybe the token is still valid
        return proceedWithRequest();
      });
  });
});

// Set up HTTP Link for queries and mutations
const httpLink = new HttpLink({
  uri: GRAPHQL_ENDPOINT,
  credentials: 'include'
});

// Add a custom link to log requests on mobile
const loggingLink = new ApolloLink((operation, forward) => {
  if (isMobileBrowser() && operation.operationName === 'TokenByBankId') {
    console.log('MOBILE: Apollo sending mutation:', {
      operationName: operation.operationName,
      variables: JSON.stringify(operation.variables, null, 2)
    });
  }
  return forward(operation);
});

// Set up SSE Link for subscriptions
const sseLink = new SSELink(GRAPHQL_ENDPOINT);

// Combine auth link with HTTP link
const authenticatedHttpLink = from([authLink, httpLink]);

// Split links based on operation type
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  sseLink,
  authenticatedHttpLink
);

// Set up Apollo Client
const client = new ApolloClient({
  link: splitLink,
  devtools: { enabled: true }, // Enable Apollo DevTools extension
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          currentCustomer: {
            // Merge incoming customer data with existing data instead of replacing
            merge(existing, incoming) {
              return { ...existing, ...incoming };
            },
          },
        },
      },
      Customer: {
        // Use customerNumber as the unique identifier for Customer objects
        keyFields: ['customerNumber'],
      },
      Card: {
        // Use id as the unique identifier for Card objects
        keyFields: ['id'],
      },
      CardType: {
        // Use id as the unique identifier for CardType objects
        keyFields: ['id'],
      },
      Transaction: {
        // Use timestamp as the unique identifier since we no longer have cardNumber
        keyFields: ['at'],
      },
      Subscription: {
        // Don't cache subscription results
        merge: false,
      },
      AuthenticationUpdate: {
        // Don't try to normalize AuthenticationUpdate objects
        merge: false,
      },
      Address: {
        // Address has no id — merge fields instead of replacing the whole object
        merge: true,
      },
      Owner: {
        // Use corporateRegistrationNumber as the unique identifier for Owner objects (id may be non-numeric UUID)
        keyFields: ['corporateRegistrationNumber'],
      },
    }
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
})

if (import.meta.env.VITE_MAINTANENCE_MODE === 'true') {
  const root = ReactDOM.createRoot(document.getElementById('App')!)
  root.render(<>Maintenance</>)
} else {
  const root = ReactDOM.createRoot(document.getElementById('App')!)
  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <BannerProvider>
            <ToastProvider>
              <ApolloProvider client={client}>
                <AuthProvider>
                  <AuthenticatedApp />
                  {import.meta.env.VITE_ENABLE_DEVTOOLS === 'true' && (
                    <>
                      <ReactQueryDevtools
                        initialIsOpen={false}
                        position="bottom-right"
                      />
                    </>
                  )}
                </AuthProvider>
              </ApolloProvider>
            </ToastProvider>
          </BannerProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </React.StrictMode>
  )
}
